import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import NavBar from "@/components/NavBar";
import StatusPill from "@/components/StatusPill";
import api, { formatApiError } from "@/lib/api";
import { taskUrgency, sortByUrgency } from "@/lib/taskUrgency";
import useAdminFirehose from "@/hooks/useAdminFirehose";
import AgentAvatar from "@/components/AgentAvatar";
import { ADMIN } from "@/constants/testIds";
import { Users, ListChecks, TrendingUp, BadgeCheck, ShieldX, ShieldCheck, ShieldAlert, AlertTriangle, Calendar, ChevronRight, Radio } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const [tab, setTab] = useState(initialTab);
  const switchTab = (next) => {
    setTab(next);
    setSearchParams(next === "overview" ? {} : { tab: next }, { replace: true });
  };
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [photoQueue, setPhotoQueue] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const [s, p, t, d, ph] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/agents/pending"),
        api.get("/admin/tasks"),
        api.get("/admin/disputes"),
        api.get("/admin/photo-queue"),
      ]);
      setStats(s.data); setPending(p.data); setTasks(t.data); setDisputes(d.data); setPhotoQueue(ph.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  // Live firehose: any task lifecycle change (create / claim / status / assign)
  // updates the in-memory tasks list without a refresh.
  const [liveTick, setLiveTick] = useState(0);
  const handleFirehose = useCallback((payload) => {
    if (!payload) return;
    if (payload.type === "task_created" && payload.data) {
      const t = payload.data;
      setTasks((prev) => {
        if (prev.some((x) => x.task_id === t.task_id)) return prev;
        return [t, ...prev];
      });
      setLiveTick((n) => n + 1);
      toast.info(`New task: ${t.title}`, {
        description: `${t.client_name || "A client"} · ${t.category_label || t.category} · ${t.city || ""}`.trim(),
        action: { label: "Open", onClick: () => navigate(`/tasks/${t.task_id}`) },
      });
    } else if (payload.type === "task_updated" && payload.data) {
      setTasks((prev) => {
        const exists = prev.some((t) => t.task_id === payload.data.task_id);
        return exists
          ? prev.map((t) => (t.task_id === payload.data.task_id ? payload.data : t))
          : [payload.data, ...prev];
      });
      setLiveTick((n) => n + 1);
    } else if (payload.type === "dispute_opened" && payload.data) {
      const t = payload.data;
      // Refresh the disputes list lazily so the open count tab stays accurate
      api.get("/admin/disputes").then((r) => setDisputes(r.data)).catch(() => {});
      setLiveTick((n) => n + 1);
      toast.error(`⚠ Dispute opened: ${t.title}`, {
        description: t.dispute?.reason ? `Reason: ${t.dispute.reason}` : `Raised on task ${t.task_id}`,
        action: { label: "Review", onClick: () => navigate(`/tasks/${t.task_id}`) },
        duration: 8000,
      });
    }
  }, [navigate]);
  useAdminFirehose({ enabled: true, onEvent: handleFirehose });

  const verify = async (user_id, approved) => {
    try {
      await api.post(`/admin/agents/${user_id}/verify`, { approved, note: approved ? "Approved" : "Rejected" });
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const resolveDispute = async (task_id, decision) => {
    try {
      await api.post(`/admin/disputes/${task_id}/resolve`, { decision, note: `Resolved by admin: ${decision}` });
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const approvePhoto = async (user_id) => {
    try {
      await api.post(`/admin/users/${user_id}/photo/approve`);
      setPhotoQueue((q) => q.filter((r) => r.user_id !== user_id));
      toast.success("Photo approved — now visible to clients");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rejectPhoto = async (user_id) => {
    const reason = window.prompt("Reason for rejecting this photo? (shown to the agent)", "Photo doesn't meet our quality / safety guidelines");
    if (reason === null) return;
    try {
      await api.post(`/admin/users/${user_id}/photo/reject`, { reason });
      setPhotoQueue((q) => q.filter((r) => r.user_id !== user_id));
      toast.success("Photo rejected — the agent can re-upload");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="eb-chip">Admin</span>
            <h1 className="mt-3 text-4xl">Operations console</h1>
          </div>
          <div
            data-testid="admin-live-indicator"
            data-tick={liveTick}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--eb-success)] px-3 py-1.5 rounded-full bg-[var(--eb-success)]/10 border border-[var(--eb-success)]/30"
            title="Live firehose connected"
          >
            <Radio className="w-3 h-3 animate-pulse" /> Live
          </div>
        </div>

        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { id: "overview", label: "Overview" },
            { id: "users", label: "Users" },
            { id: "agents", label: `Pending agents (${pending.length})` },
            { id: "photos", label: `Photo queue (${photoQueue.length})` },
            { id: "disputes", label: `Disputes (${disputes.length})` },
            { id: "tasks", label: "All tasks" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`${ADMIN.tab}-${t.id}`}
              onClick={() => switchTab(t.id)}
              className={`eb-btn !py-2 !px-4 !text-xs uppercase tracking-[0.16em] ${tab === t.id ? "eb-btn-primary" : "eb-btn-ghost"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          stats ? (
            <div className="grid md:grid-cols-3 gap-5">
              <StatCard icon={Users} label="Total users" value={stats.users.total} sub={`${stats.users.clients} clients · ${stats.users.agents} agents`} />
              <StatCard icon={ListChecks} label="Tasks" value={stats.tasks.total} sub={`${stats.tasks.in_flight} in flight · ${stats.tasks.completed} completed`} />
              <StatCard icon={TrendingUp} label="GMV" value={`₦${(stats.gmv.ngn || 0).toLocaleString()}`} sub={`$${(stats.gmv.usd || 0).toFixed(2)} via Stripe`} />
              <StatCard icon={BadgeCheck} label="Verified agents" value={stats.users.verified_agents} sub="Live in marketplace" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-5">
              {[1,2,3].map((i) => <div key={i} className="eb-card p-6 animate-pulse h-32" />)}
            </div>
          )
        )}

        {tab === "users" && <UsersTab />}

        {tab === "agents" && (
          <div className="space-y-4">
            {pending.length === 0 ? (
              <div className="eb-card p-8 text-[var(--eb-text-dim)]">No agents awaiting verification.</div>
            ) : pending.map((a) => (
              <div key={a.user_id} className="eb-card p-6 flex items-start justify-between gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{a.verification_status?.replace("_", " ")}</div>
                  <div className="mt-1 text-lg font-[700]">{a.name}</div>
                  <div className="text-sm text-[var(--eb-text-dim)]">{a.email} · {a.phone || "no phone"}</div>
                  <div className="text-sm mt-2 text-[var(--eb-text-dim)]">{a.agent_profile?.bio || "No bio submitted"}</div>
                  <div className="text-xs mt-2 text-[var(--eb-text-faint)]">Cities: {(a.agent_profile?.city_coverage || []).join(", ") || "—"}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={ADMIN.approveAgent} onClick={() => verify(a.user_id, true)} className="eb-btn eb-btn-primary !py-2 !px-3 !text-xs"><ShieldCheck className="w-4 h-4" /> Approve</button>
                  <button data-testid={ADMIN.rejectAgent} onClick={() => verify(a.user_id, false)} className="eb-btn eb-btn-danger !py-2 !px-3 !text-xs"><ShieldX className="w-4 h-4" /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "photos" && (
          <div className="space-y-4">
            {photoQueue.length === 0 ? (
              <div className="eb-card p-8 text-[var(--eb-text-dim)]" data-testid="admin-photo-queue-empty">
                No photos awaiting review. New agent uploads will appear here.
              </div>
            ) : photoQueue.map((p) => (
              <div
                key={p.user_id}
                data-testid={ADMIN.photoQueueRow}
                data-user-id={p.user_id}
                className="eb-card p-6 flex items-start justify-between gap-6 flex-wrap"
              >
                <div className="flex items-start gap-5 flex-1 min-w-0">
                  <div className="flex flex-col items-center gap-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">Current</div>
                    <AgentAvatar src={p.current_photo_url} name={p.name} size={72} testid="admin-photo-current" />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-secondary)]">Pending</div>
                    <AgentAvatar src={p.pending_photo_url} name={p.name} size={72} testid="admin-photo-pending" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{p.verification_status?.replace("_", " ") || "agent"}</div>
                    <div className="mt-1 text-lg font-[700] truncate">{p.name}</div>
                    <div className="text-sm text-[var(--eb-text-dim)] truncate">{p.email}</div>
                    {p.pending_since && (
                      <div className="text-xs mt-2 text-[var(--eb-text-faint)]">
                        Submitted {new Date(p.pending_since).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={ADMIN.approvePhoto}
                    onClick={() => approvePhoto(p.user_id)}
                    className="eb-btn eb-btn-primary !py-2 !px-3 !text-xs"
                  >
                    <ShieldCheck className="w-4 h-4" /> Approve
                  </button>
                  <button
                    data-testid={ADMIN.rejectPhoto}
                    onClick={() => rejectPhoto(p.user_id)}
                    className="eb-btn eb-btn-danger !py-2 !px-3 !text-xs"
                  >
                    <ShieldX className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "disputes" && (
          <div className="space-y-3">
            {disputes.length === 0 ? (
              <div className="eb-card p-8 text-[var(--eb-text-dim)]">No open disputes — well done team.</div>
            ) : disputes.map((t) => (
              <div key={t.task_id} className="eb-card p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-[var(--eb-secondary)]" />
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{t.category_label} · {t.city} · {t.task_id}</span>
                    </div>
                    <div className="font-[700] text-lg mt-1">{t.title}</div>
                    <div className="text-xs text-[var(--eb-text-dim)] mt-1">{t.client_name} → {t.agent_name || "unassigned"}</div>
                    <p className="mt-3 text-sm text-[var(--eb-text)]">&ldquo;{t.dispute?.reason}&rdquo;</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button data-testid={ADMIN.resolveRefund} onClick={() => resolveDispute(t.task_id, "refund_client")} className="eb-btn eb-btn-danger !py-2 !px-3 !text-xs">Refund client</button>
                    <button data-testid={ADMIN.resolveRelease} onClick={() => resolveDispute(t.task_id, "release_to_agent")} className="eb-btn eb-btn-primary !py-2 !px-3 !text-xs">Release to agent</button>
                    <button data-testid={ADMIN.resolveNone} onClick={() => resolveDispute(t.task_id, "none")} className="eb-btn eb-btn-ghost !py-2 !px-3 !text-xs">No fault</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "tasks" && (
          <div className="space-y-3">
            {tasks.length === 0 ? (
              <div className="eb-card p-8 text-[var(--eb-text-dim)]">No tasks yet.</div>
            ) : sortByUrgency(tasks).map((t) => {
              const u = taskUrgency(t);
              return (
                <Link
                  key={t.task_id}
                  to={`/tasks/${t.task_id}`}
                  data-testid="admin-task-row"
                  data-urgency={u.bucket}
                  className={`eb-card p-5 flex items-center justify-between gap-3 relative hover:border-[var(--eb-primary)] transition-colors ${u.ringClass} ${u.bgClass}`}
                >
                  <span className={`absolute top-3 left-3 w-2 h-2 rounded-full ${u.dotClass}`} aria-hidden="true" />
                  <div className="pl-4 flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{t.category_label} · {t.city}</div>
                    <div className="font-[700] mt-1 truncate">{t.title}</div>
                    <div className="text-xs text-[var(--eb-text-dim)] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span>{t.client_name} → {t.agent_name || "unassigned"}</span>
                      {t.deadline && (
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(t.deadline).toLocaleDateString()}</span>
                      )}
                      {u.label && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] border ${u.badgeClass}`}>
                          {u.bucket === "late" && <AlertTriangle className="w-3 h-3" />}
                          {u.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <StatusPill status={t.status} />
                    <ChevronRight className="w-4 h-4 text-[var(--eb-text-dim)]" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {err && <div className="mt-6 text-[var(--eb-secondary)] text-sm">{err}</div>}
        {busy && tab !== "overview" && <div className="mt-3 text-[var(--eb-text-dim)] text-sm">Loading…</div>}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="eb-card p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{label}</span>
        <Icon className="w-5 h-5 text-[var(--eb-primary)]" />
      </div>
      <div className="mt-3 text-4xl font-[800] tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-[var(--eb-text-dim)]">{sub}</div>
    </div>
  );
}


function UsersTab() {
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setBusy(true);
    const params = new URLSearchParams();
    if (roleFilter) params.set("role", roleFilter);
    if (search) params.set("search", search);
    const q = params.toString();
    api.get(`/admin/users${q ? `?${q}` : ""}`)
      .then((r) => { if (active) setUsers(r.data); })
      .catch(() => { if (active) setUsers([]); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [roleFilter, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="admin-users-search"
          type="search"
          placeholder="Search by name, email or phone…"
          className="eb-input max-w-md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex gap-2">
          {[
            { v: "", l: "Everyone" },
            { v: "client", l: "Clients" },
            { v: "agent", l: "Agents" },
            { v: "admin", l: "Admins" },
          ].map((f) => (
            <button
              key={f.v}
              data-testid={`admin-users-filter-${f.v || "all"}`}
              onClick={() => setRoleFilter(f.v)}
              className={`eb-btn !py-1.5 !px-3 !text-[11px] uppercase tracking-[0.16em] ${roleFilter === f.v ? "eb-btn-primary" : "eb-btn-ghost"}`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-[var(--eb-text-dim)]">
          {busy ? "Loading…" : `${users.length} result${users.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className="grid gap-3">
        {users.length === 0 && !busy && (
          <div className="eb-card p-8 text-[var(--eb-text-dim)]">No users match these filters.</div>
        )}
        {users.map((u) => (
          <Link
            key={u.user_id}
            to={`/admin/users/${u.user_id}`}
            data-testid="admin-users-row"
            data-role={u.role}
            className="eb-card p-5 flex items-center gap-3 hover:border-[var(--eb-primary)] transition-colors"
          >
            <AgentAvatar src={u.photo_url} name={u.name} size={44} />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">
                {u.role}
                {u.role === "agent" && u.verification_status && ` · ${u.verification_status}`}
                {u.suspended && <span className="ml-2 text-red-700">· SUSPENDED</span>}
              </div>
              <div className="mt-1 font-[700] truncate">{u.name}</div>
              <div className="text-xs text-[var(--eb-text-dim)] mt-1 flex flex-wrap gap-x-3 gap-y-1">
                <span>{u.email}</span>
                {u.phone && <span>· {u.phone}</span>}
                {u.country && <span>· {u.country}</span>}
              </div>
            </div>
            {u.role === "agent" ? (
              <div className="text-right shrink-0 text-xs text-[var(--eb-text-dim)]">
                <div>★ {(u.rating || 0).toFixed(1)} · {u.completed_tasks} done</div>
                <div className="text-[10px] uppercase tracking-[0.16em] mt-0.5">{u.verification_status}</div>
              </div>
            ) : (
              <ChevronRight className="w-4 h-4 text-[var(--eb-text-dim)] shrink-0" />
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
